import { makeLookupTreeFromItems, resolveAllImplicitLinks, textHasMatch } from "../biz/linkFinder";
import { SquiggleItem } from "../types/types";

export type ItemId = string & { readonly '': unique symbol };
export type DirectedEdge = { source: ItemId; target: ItemId; implicit: boolean };

// Neighbour links point from an open item to a non-open item it references, so the non-open item can be shown dimmed.
export const findNeighbourHierarchyLinks = (
  openItems: SquiggleItem[],
  openItemIds: Set<ItemId>,
  itemById: Map<string, SquiggleItem>,
  childrenByParentId: Map<string, SquiggleItem[]>,
  includeItem: (item: SquiggleItem) => boolean,
  neighbourDirectedEdges: Map<string, DirectedEdge>,
  neighbourItemIds: Set<ItemId>
) => {
  for (const item of openItems) {
    if (item.parent && !openItemIds.has(item.parent as ItemId)) {
      const parentItem = itemById.get(item.parent);
      if (parentItem && includeItem(parentItem)) {
        const parentId = item.parent as ItemId;
        const edgeKey = `${item.id}->${parentId}`;
        neighbourDirectedEdges.set(edgeKey, { source: item.id as ItemId, target: parentId, implicit: false });
        neighbourItemIds.add(parentId);
      }
    }

    for (const child of childrenByParentId.get(item.id) ?? []) {
      const childId = child.id as ItemId;
      if (!openItemIds.has(childId) && includeItem(child)) {
        const edgeKey = `${childId}->${item.id}`;
        neighbourDirectedEdges.set(edgeKey, { source: childId, target: item.id as ItemId, implicit: false });
        neighbourItemIds.add(childId);
      }
    }
  }
};

export const findNeighbourExplicitLinks = (
  openItems: SquiggleItem[],
  openItemIds: Set<ItemId>,
  itemById: Map<string, SquiggleItem>,
  includeItem: (item: SquiggleItem) => boolean,
  neighbourDirectedEdges: Map<string, DirectedEdge>,
  neighbourItemIds: Set<ItemId>
) => {
  for (const sourceItem of openItems) {
    for (const linkId of sourceItem.links) {
      const targetId = linkId as ItemId;
      if (targetId === sourceItem.id || openItemIds.has(targetId)) {
        continue;
      }
      const targetItem = itemById.get(targetId);
      if (!targetItem || !includeItem(targetItem)) {
        continue;
      }
      const edgeKey = `${sourceItem.id}->${targetId}`;
      if (!neighbourDirectedEdges.has(edgeKey)) {
        neighbourDirectedEdges.set(edgeKey, { source: sourceItem.id as ItemId, target: targetId, implicit: false });
        neighbourItemIds.add(targetId);
      }
    }
  }
};

export const findNeighbourImplicitLinks = (
  openItems: SquiggleItem[],
  allItems: SquiggleItem[],
  openItemIds: Set<ItemId>,
  itemById: Map<string, SquiggleItem>,
  includeItem: (item: SquiggleItem) => boolean,
  itemLookupTree: ReturnType<typeof makeLookupTreeFromItems>,
  neighbourDirectedEdges: Map<string, DirectedEdge>,
  neighbourItemIds: Set<ItemId>
) => {
  for (const sourceItem of openItems) {
    const implicitLinks = resolveAllImplicitLinks(itemLookupTree, sourceItem.text, sourceItem.id);

    for (const { id } of implicitLinks) {
      const targetId = id as ItemId;
      if (targetId === sourceItem.id || openItemIds.has(targetId)) {
        continue;
      }
      const targetItem = itemById.get(targetId);
      if (!targetItem || !includeItem(targetItem)) {
        continue;
      }
      const edgeKey = `${sourceItem.id}->${targetId}`;
      if (!neighbourDirectedEdges.has(edgeKey)) {
        neighbourDirectedEdges.set(edgeKey, { source: sourceItem.id as ItemId, target: targetId, implicit: true });
        neighbourItemIds.add(targetId);
      }
    }

    // This should be optimized for performance
    const thisLookupTree = makeLookupTreeFromItems([sourceItem]);

    for (let otherItem of allItems) {
      if (!includeItem(otherItem) || openItemIds.has(otherItem.id as ItemId)) {
        continue;
      }

      if (sourceItem.id !== otherItem.id) {
        if (textHasMatch(thisLookupTree, otherItem.text)) {
          const edgeKey = `${otherItem.id}->${sourceItem.id}`;
          // Not great, but a way to avoid directed edges both ways.
          // We should instead detect double edges and make them non-directed.
          // This goes for all of the edges found.
          const reverseKey = `${sourceItem.id}->${otherItem.id}`;
          if (!neighbourDirectedEdges.has(edgeKey) && !neighbourDirectedEdges.has(reverseKey)) {
            neighbourDirectedEdges.set(edgeKey, { source: otherItem.id as ItemId, target: sourceItem.id as ItemId, implicit: true });
            neighbourItemIds.add(otherItem.id as ItemId);
          }
        }
      }
    }
  }
};